import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Trophy, Star, Medal, User, Printer } from "lucide-react";
import api from "@/lib/api";
import { printLeaderboard } from "@/lib/printLeaderboard";
import { PageHero } from "@/components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Leaderboards() {
  const [params] = useSearchParams();
  const seasonParam = params.get("season");
  const [filters, setFilters] = useState({ venues: [], seasons: [], events: [] });
  const [season, setSeason] = useState(seasonParam || "all");
  const [venue, setVenue] = useState("all");
  const [event, setEvent] = useState("all");
  const [sort, setSort] = useState("points");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/leaderboards/filters").then((r) => {
      setFilters(r.data);
      if (!seasonParam && r.data.default_season) setSeason(r.data.default_season);
    }).catch(() => {});
  }, [seasonParam]);

  useEffect(() => { setEvent("all"); }, [venue, season]);

  const load = useCallback(() => {
    api.get(`/leaderboards?season=${encodeURIComponent(season)}&venue=${encodeURIComponent(venue)}&event=${encodeURIComponent(event)}&sort=${sort}`).then((r) => setRows(r.data)).catch(() => {});
  }, [venue, season, event, sort]);

  useEffect(() => { load(); }, [load]);

  const seasonLabel = (s) => { const r = [s.start_date, s.end_date].filter(Boolean).join(" – "); return r ? `${s.name} (${r})` : s.name; };
  const eventOptions = [...new Map((filters.events || []).filter((e) => (venue === "all" || e.venue === venue) && (season === "all" || e.season === season)).map((e) => [e.title, e])).values()];

  const rankColor = (r) => (r === 1 ? "text-gold" : r === 2 ? "text-slate-300" : r === 3 ? "text-amber-700" : "text-slate-500");
  const avatar = (p) => (p ? (p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`) : "");

  return (
    <div data-testid="leaderboards-page">
      <PageHero
        title="Leaderboards"
        subtitle="Track the season standings across every venue. The top qualifiers earn their seat at the Legends Championship Main Event."
        image="https://images.unsplash.com/photo-1514820720301-4c4790309f46?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        testId="leaderboards-hero"
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger className="w-full sm:w-72 rounded-none bg-navy-paper border-slate-700" data-testid="season-filter"><SelectValue placeholder="Season" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Seasons</SelectItem>
              {filters.seasons.map((s) => <SelectItem key={s.name} value={s.name}>{seasonLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={venue} onValueChange={setVenue}>
            <SelectTrigger className="w-full sm:w-56 rounded-none bg-navy-paper border-slate-700" data-testid="venue-filter"><SelectValue placeholder="Venue" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Venues</SelectItem>
              {filters.venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger className="w-full sm:w-64 rounded-none bg-navy-paper border-slate-700" data-testid="event-filter"><SelectValue placeholder="Event" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {eventOptions.map((e) => <SelectItem key={e.title} value={e.title}>{e.title}{e.date ? ` · ${e.date}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="board-toggle">
          {[["points", "Overall (Points)"], ["final_tables", "Most Final Tables"]].map(([val, label]) => (
            <button key={val} onClick={() => setSort(val)} data-testid={`sort-${val}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${sort === val ? "bg-gold text-navy border-gold" : "border-slate-700 text-slate-300 hover:border-gold"}`}>
              {label}
            </button>
          ))}
          <button
            onClick={() => printLeaderboard({ title: season === "all" ? "Overall Standings" : season, subtitle: `${venue === "all" ? "All Venues" : venue} \u00b7 ${rows.length} players`, rows })}
            data-testid="print-leaderboard"
            className="sm:ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-gold/40 text-gold hover:bg-gold hover:text-navy transition-colors">
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="leaderboard-table">
          <table className="w-full text-left min-w-[720px]">
            <thead className="bg-secondary sticky top-0">
              <tr className="text-gold text-xs uppercase tracking-wider">
                <th className="px-5 py-4 w-16">Rank</th>
                <th className="px-5 py-4">Player</th>
                <th className="px-5 py-4 text-right">Points</th>
                <th className="px-5 py-4 text-right">Final Tables</th>
                <th className="px-5 py-4 text-right">Played</th>
                <th className="px-5 py-4 text-right">Wins</th>
                <th className="px-5 py-4 text-right">Payouts</th>
                <th className="px-5 py-4 text-center">Finals</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.player_name} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`lb-row-${row.rank}`}>
                  <td className={`px-5 py-4 font-display text-xl font-bold ${rankColor(row.rank)}`}>
                    <span className="flex items-center gap-1">
                      {row.rank <= 3 && <Medal className="w-4 h-4" />}{row.rank}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-white font-medium">
                    <Link to={`/players/${encodeURIComponent(row.player_name)}`} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid={`lb-player-${row.rank}`}>
                      <span className="w-8 h-8 rounded-full overflow-hidden bg-navy border border-white/10 flex items-center justify-center flex-shrink-0">
                        {avatar(row.picture) ? <img src={avatar(row.picture)} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-slate-500" />}
                      </span>
                      {row.player_name}
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-right text-gold font-bold">{row.points}</td>
                  <td className="px-5 py-4 text-right text-slate-200 font-semibold">{row.final_tables}</td>
                  <td className="px-5 py-4 text-right text-slate-400">{row.events_played}</td>
                  <td className="px-5 py-4 text-right text-slate-400">{row.wins}</td>
                  <td className="px-5 py-4 text-right text-slate-300">${row.total_payout?.toLocaleString()}</td>
                  <td className="px-5 py-4 text-center">
                    {row.qualifier && <Star className="w-4 h-4 text-gold inline drop-shadow-[0_0_6px_rgba(212,175,55,0.6)]" title="Qualified for finals" />}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-500">No standings for this selection yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-slate-500 text-xs mt-4 flex items-center gap-2"><Star className="w-3.5 h-3.5 text-gold" /> Qualified for the season Main Event.</p>
      </div>
    </div>
  );
}
